/**
 * CareMap splash split-flap: "MEDICAL SUPERINTELLIGENCE" + superscript "3".
 * The "3" is its own split-flap display (flip tile), positioned as superscript (smaller, raised).
 * Primary #1B4584, lighter #2458A6. Used at top of MapPage (no drawer/menu).
 * No black frame: display fits within the bar height (per TOP_OF_PAGE_SPLITFLAP_BY_ROLE).
 */
import React, { useState, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { tokens } from "@healthdesk/shared-utils";
import { SplitFlapRow } from "../../features/facility/components/queue/splitflap/QueueDisplay";

/** 3D split-flap metrics — compact CareMap title bar (flap + tagline). */
const DEFAULT_DISPLAY_3D = {
  perspectivePx: 220,
  perspectiveOriginYPercent: 42,
  flapTranslateZPx: 1,
  seamTranslateZPx: 1,
  capTranslateZPx: 2,
  showThicknessEdge: true,
  phaseLighting: true,
  seamHeightPx: 2,
  seamGradient:
    "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(70,70,70,0.18) 35%, rgba(0,0,0,0.48) 100%)",
  hingeHoldMs: 18,
  charJitterMs: 1,
  edgeDarkening: true,
  edgeDarkeningStrength: 0.18,
  bottomOvershootDeg: 4,
  capWidthPx: 3,
  capHeightPx: 6,
};

const FLIP_DURATION_MS = 52;
const SPEED_MS = 46;
const CHAR_STAGGER_MS = 14;
const CHARACTER_COLOR = "common.white";
const BOARD_BG = tokens.colors.navGlobalManagerBoard.light;
/** CareMap splash: Primary (bottom flap) - MSI Primary Blue */
const CAREMAP_LOWER = "primary.main";
/** CareMap splash: Lighter (top flap) - MSI Primary Blue */
const CAREMAP_UPPER = tokens.colors.msiBlueUpper.light;
/** Main row tiles — compact bar (shorter than prior 0.5× pass). */
const TILE_WIDTH = "clamp(17px, 2.2vw, 31px)";
const TILE_HEIGHT = "clamp(22px, 2.85vw, 38px)";
/** Narrow layout: smaller tiles so "MEDICAL" (7) and "SUPERINTELLIGENCE" (16) fit on screen. */
const TILE_WIDTH_NARROW = "clamp(6px, 1.85vw, 12px)";
const TILE_HEIGHT_NARROW = "clamp(9px, 2.25vw, 17px)";
/** Superscript "3" tile (smaller than main, but large enough to be visible). */
const TILE_SUP_WIDTH = "clamp(15px, 2vw, 24px)";
const TILE_SUP_HEIGHT = "clamp(17px, 2.15vw, 27px)";
const TILE_SUP_WIDTH_NARROW = "clamp(10px, 2.65vw, 18px)";
const TILE_SUP_HEIGHT_NARROW = "clamp(12px, 3.1vw, 21px)";

/**
 * CareMap split-flap: "MEDICAL SUPERINTELLIGENCE" + superscript "3" (3 has its own flip tile).
 * No black frame/padding box so height stays within bar (tile + 8px only).
 * @param {{ onRowComplete?: () => void, use3dFlip?: boolean, display3DConfig?: object, flipDurationMs?: number, linkSuperscriptTo?: string }} props
 *   linkSuperscriptTo: if set, the superscript "3" is wrapped in a link to this path (e.g. "/msi3"); state.from is set to current path for Back.
 */
function CareMapSplitFlap({
  onRowComplete,
  use3dFlip = true,
  display3DConfig = DEFAULT_DISPLAY_3D,
  flipDurationMs = FLIP_DURATION_MS,
  linkSuperscriptTo,
}) {
  const theme = useTheme();
  const location = useLocation();
  /* Use 'md' (900px) so phones and tablets get narrow layout; avoids overflow on iOS. */
  const narrowLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [mainAnimationComplete, setMainAnimationComplete] = useState(false);
  const handleMainRowComplete = useCallback(() => {
    setMainAnimationComplete(true);
    onRowComplete?.();
  }, [onRowComplete]);

  /* Fallback: if onRowComplete never fires (e.g. main row completion check fails), start "3" animation after a delay. */
  useEffect(() => {
    const t = setTimeout(() => setMainAnimationComplete((prev) => prev || true), 8000);
    return () => clearTimeout(t);
  }, []);

  const superscript3Box = (
    <Box
      sx={{
        position: "relative",
        zIndex: 2,
        ...(narrowLayout
          ? { marginLeft: "2px", transform: "translateY(-50%)" }
          : { marginLeft: "6px", transform: "translateY(-50%)" }),
        minWidth: narrowLayout ? 12 : 24,
        minHeight: narrowLayout ? 14 : 24,
        width: narrowLayout ? TILE_SUP_WIDTH_NARROW : TILE_SUP_WIDTH,
        height: narrowLayout ? TILE_SUP_HEIGHT_NARROW : TILE_SUP_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "visible",
      }}
      aria-hidden={!linkSuperscriptTo}
    >
      <SplitFlapRow
        key={mainAnimationComplete ? "superscript-3-go" : "superscript-3-wait"}
        textSegments={mainAnimationComplete ? ["3"] : [" "]}
        speeds={[SPEED_MS]}
        rowIndex={0}
        isClock={true}
        compact={false}
        segmentBgColor="transparent"
        segmentUpperFlapColor={CAREMAP_UPPER}
        segmentLowerFlapColor={CAREMAP_LOWER}
        characterColor={CHARACTER_COLOR}
        fontWeight={700}
        charStaggerMs={0}
        flipDurationMs={flipDurationMs}
        use3dFlip={use3dFlip}
        boardBgColor={BOARD_BG}
        display3DConfig={display3DConfig}
        tileWidth={narrowLayout ? TILE_SUP_WIDTH_NARROW : TILE_SUP_WIDTH}
        tileHeight={narrowLayout ? TILE_SUP_HEIGHT_NARROW : TILE_SUP_HEIGHT}
        showCharacterOverlay={true}
      />
      {linkSuperscriptTo && (
        <Link
          to={{ pathname: linkSuperscriptTo, state: { from: location.pathname || "/" } }}
          aria-label="Learn about Medical SuperIntelligence"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        />
      )}
    </Box>
  );

  const mainRowProps = {
    speeds: [SPEED_MS],
    isClock: true,
    compact: false,
    segmentBgColor: "transparent",
    segmentUpperFlapColor: CAREMAP_UPPER,
    segmentLowerFlapColor: CAREMAP_LOWER,
    characterColor: CHARACTER_COLOR,
    fontWeight: 700,
    charStaggerMs: CHAR_STAGGER_MS,
    flipDurationMs,
    use3dFlip,
    boardBgColor: BOARD_BG,
    display3DConfig,
    showCharacterOverlay: true,
  };

  const taglineSx = {
    fontFamily: '"Federo", Georgia, serif',
    color: "common.white",
    fontSize: narrowLayout ? "clamp(8px, 2.35vw, 11px)" : "clamp(10px, 1.45vw, 13px)",
    fontWeight: 400,
    letterSpacing: "0.02em",
    textAlign: "center",
    margin: 0,
    /* Slight gap + one tight line above copy (shorter bar). */
    marginTop: narrowLayout ? "calc(1px + 0.9em)" : "calc(2px + 0.9em)",
    lineHeight: 1.2,
    maxWidth: "min(960px, calc(100vw - 24px))",
    flexShrink: 0,
    paddingLeft: "6px",
    paddingRight: "6px",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        padding: narrowLayout ? "0 3px 1px 3px" : "0 2px 1px 2px",
      }}
    >
      <Box
        sx={{
          color: CHARACTER_COLOR,
          display: "flex",
          flexDirection: narrowLayout ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "nowrap",
          gap: narrowLayout ? "1px" : "2px",
          flexShrink: 0,
          maxHeight: narrowLayout
            ? "max(60px, calc(5px + 2 * (3px + max(clamp(9px, 2.25vw, 17px), clamp(6px, 1.85vw, 12px))) + 3px))"
            : "max(46px, calc(1.45 * (3px + max(clamp(22px, 2.85vw, 38px), clamp(14px, 2vw, 24px)))))",
        }}
        aria-label="Medical SuperIntelligence cubed"
      >
        {narrowLayout ? (
          <>
            <SplitFlapRow
              textSegments={["MEDICAL"]}
              rowIndex={0}
              {...mainRowProps}
              tileWidth={TILE_WIDTH_NARROW}
              tileHeight={TILE_HEIGHT_NARROW}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1px",
                flexWrap: "nowrap",
              }}
            >
              <SplitFlapRow
                textSegments={["SUPERINTELLIGENCE"]}
                rowIndex={1}
                onRowComplete={handleMainRowComplete}
                {...mainRowProps}
                tileWidth={TILE_WIDTH_NARROW}
                tileHeight={TILE_HEIGHT_NARROW}
              />
              {superscript3Box}
            </Box>
          </>
        ) : (
          <>
            <SplitFlapRow
              textSegments={["MEDICAL SUPERINTELLIGENCE"]}
              rowIndex={0}
              onRowComplete={handleMainRowComplete}
              {...mainRowProps}
              tileWidth={TILE_WIDTH}
              tileHeight={TILE_HEIGHT}
            />
            {/* Wide: superscript "3" to the right (translateY -50%). */}
            {superscript3Box}
          </>
        )}
      </Box>
      <Typography component="p" sx={taglineSx}>
        Elevating Human Intelligence in Healthcare
      </Typography>
    </Box>
  );
}

export default CareMapSplitFlap;
